import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import video1 from '@/assets/videos/video1.mp4'
import video3 from '@/assets/videos/video3.mp4'
import video2 from '@/assets/videos/video2.mp4'
import flag from '@/assets/images/flag.svg'

const testimonials = [
  { id: 1, name: 'U.Steve', project: 'Project: Chaser', video: video1, flag: flag },
  { id: 2, name: 'Jennifer', project: 'Project: LucidDreams', video: video3, flag: flag },
  { id: 3, name: 'Gabriel', project: 'Project: LucidDreams', video: video3, flag: flag },
  { id: 4, name: 'Josh', project: 'Project: LucidDreams', video: video2, flag: flag },
  { id: 5, name: 'Polly', project: 'Project: LucidDreams', video: video1, flag: flag },
]

const isMobile = () => window.innerWidth < 1024

export default function TestimonialsSection() {
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const [activeItem, setActiveItem] = useState<Item | null>(null)
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({})

  const handleMouseEnter = (id: number) => {
    // Stop all videos
    Object.entries(videoRefs.current).forEach(([key, vid]) => {
      if (Number(key) !== id && vid) {
        vid.pause()
        vid.currentTime = 0
      }
    })
    setHoveredId(id)
    const vid = videoRefs.current[id]
    if (vid) {
      vid.play().catch(() => {})
    }
  }

  const handleMouseLeave = (id: number) => {
    setHoveredId(null)
    const vid = videoRefs.current[id]
    if (vid) {
      vid.pause()
      vid.currentTime = 0
    }
  }

  const [first, ...rest] = testimonials

  return (
    <section
      className="bg-white pt-16 sm:pt-24 xl:pt-[200px]"
      aria-labelledby="testimonials-heading"
    >
      <div className="container-site">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex flex-col gap-3 sm:gap-4 lg:mb-10 lg:gap-6"
        >
          <span className="font-sans text-[13px] font-bold uppercase tracking-[0.061px] text-[#D4575B] sm:text-[15px] lg:text-[18px]">
            Testimonials
          </span>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between lg:gap-6">
            <h2 className="max-w-[994px] font-sans text-[26px] font-medium leading-[30px] xl:tracking-[-0.5px] sm:text-[34px] sm:leading-[38px] md:text-[40px] md:leading-[44px] lg:text-[48px] lg:leading-[55px] lg:tracking-[-1px]">
              <span className="text-[#2D2D2D]">Hear From Our Clients.</span>{' '}
              <span className="text-[#979AC0]">
                Real experiences from businesses we’ve partnered with to build and scale their
                products.
              </span>
            </h2>
            <div className="shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-[10px] rounded-[10px] border border-[rgba(41,41,41,0.09)] bg-[#302F2F] p-2 md:p-4 text-center font-sans text-[18px] font-normal not-italic leading-normal text-white transition-colors hover:bg-black"
                style={{ leadingTrim: 'both', textEdge: 'cap' } as React.CSSProperties}
              >
                Get on a call
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Grid */}
        <div className="flex flex-col gap-4 sm:gap-6">
          {/* Top row: large first card + second card */}
          <div className="flex flex-col gap-4 sm:gap-6 xl:flex-row">
            {/* First big card */}
            <div
              className="relative overflow-hidden rounded-[10px] cursor-pointer w-full h-[240px] sm:h-[340px] md:h-[400px] lg:h-[440px] xl:shrink-0 xl:max-w-[798px] xl:h-[488px]"
              onMouseEnter={() => handleMouseEnter(first.id)}
              onMouseLeave={() => handleMouseLeave(first.id)}
              onClick={() => setActiveItem(first)}
            >
              <video
                ref={(el) => {
                  videoRefs.current[first.id] = el
                  if (el && isMobile()) el.play().catch(() => {})
                }}
                src={first.video}
                muted
                loop
                playsInline
                preload="auto"
                autoPlay={isMobile()}
                className="w-full h-full object-cover"
              >
                <track kind="captions" src="/empty.vtt" srcLang="en" label="English" default />
              </video>
              {/* Bottom gradient overlay */}
              <div
                className="absolute bottom-0 left-0 right-0 h-40 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                  opacity: hoveredId === first.id ? 0 : 1,
                }}
              />
              {/* Info overlay — always visible */}
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 flex items-end justify-between">
                <div>
                  <p className="font-sans text-[16px] sm:text-[20px] xl:text-[24px] font-normal not-italic xl:tracking-[-1px] text-brand-white">
                    {first.name}
                  </p>
                  <p className="font-sans text-[12px] sm:text-[14px] xl:text-[16px] font-normal not-italic xl:tracking-[-1px] text-brand-white">
                    {first.project}
                  </p>
                </div>
                <Flag
                  flag={first.flag}
                  className="h-[24px] w-[36px] sm:h-[30px] sm:w-[44px] xl:h-[36px] xl:w-[52px]"
                />
              </div>
            </div>

            {/* Second card */}
            <TestimonialCard
              item={rest[0]}
              hovered={hoveredId === rest[0].id}
              onEnter={handleMouseEnter}
              onLeave={handleMouseLeave}
              onOpen={setActiveItem}
              videoRefs={videoRefs}
            />
          </div>

          {/* Bottom row: three cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {rest.slice(1).map((item) => (
              <TestimonialCard
                key={item.id}
                item={item}
                hovered={hoveredId === item.id}
                onEnter={handleMouseEnter}
                onLeave={handleMouseLeave}
                onOpen={setActiveItem}
                videoRefs={videoRefs}
              />
            ))}
          </div>
        </div>
      </div>

      <VideoModal item={activeItem} onClose={() => setActiveItem(null)} />
    </section>
  )
}

type Item = (typeof testimonials)[number]

function Flag({ flag, className }: { flag: string; className?: string }) {
  if (flag.includes('/')) {
    return <img src={flag} alt="" className={className ?? 'h-[36px] w-[52px]'} />
  }
  return <span className={className ?? 'text-xl'}>{flag}</span>
}

function TestimonialCard({
  item,
  hovered,
  onEnter,
  onLeave,
  onOpen,
  videoRefs,
}: {
  item: Item
  hovered: boolean
  onEnter: (id: number) => void
  onLeave: (id: number) => void
  onOpen: (item: Item) => void
  videoRefs: React.MutableRefObject<Record<number, HTMLVideoElement | null>>
}) {
  return (
    <div
      className="relative overflow-hidden rounded-[10px] cursor-pointer flex-1 w-full h-[240px] sm:h-[340px] md:h-[400px] lg:h-[440px] xl:h-[488px]"
      onMouseEnter={() => onEnter(item.id)}
      onMouseLeave={() => onLeave(item.id)}
      onClick={() => onOpen(item)}
    >
      <video
        ref={(el) => {
          // eslint-disable-next-line react-hooks/immutability -- store the video element in a ref map
          videoRefs.current[item.id] = el
          if (el && isMobile()) el.play().catch(() => {})
        }}
        src={item.video}
        muted
        loop
        playsInline
        preload="auto"
        autoPlay={isMobile()}
        className="w-full h-full object-cover"
      >
        <track kind="captions" src="/empty.vtt" srcLang="en" label="English" default />
      </video>
      {/* Bottom gradient overlay */}
      <div
        className="absolute bottom-0 left-0 right-0 h-40 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
          opacity: hovered ? 0 : 1,
        }}
      />
      {/* Info overlay — always visible */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 flex items-end justify-between">
        <div>
          <p className="font-sans text-[16px] sm:text-[20px] xl:text-[24px] font-normal not-italic tracking-[-1px] text-brand-white">
            {item.name}
          </p>
          <p className="font-sans text-[12px] sm:text-[14px] xl:text-[16px] font-normal not-italic tracking-[-1px] text-brand-white">
            {item.project}
          </p>
        </div>
        <Flag
          flag={item.flag}
          className="h-[24px] w-[36px] sm:h-[30px] sm:w-[44px] xl:h-[36px] xl:w-[52px]"
        />
      </div>
    </div>
  )
}

function VideoModal({ item, onClose }: { item: Item | null; onClose: () => void }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!item) return
    const t = requestAnimationFrame(() => setVisible(true))
    document.body.style.overflow = 'hidden'
    return () => {
      cancelAnimationFrame(t)
      document.body.style.overflow = ''
    }
  }, [item])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 300)
  }

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose()
  }

  if (!item) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: visible ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0)',
        transition: 'background-color 0.3s ease',
      }}
      onClick={handleBackdrop}
    >
      <div
        className="relative w-full max-w-[900px] overflow-hidden rounded-[12px] bg-white shadow-2xl"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.96)',
          transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-[8px] border-[1.2px] border-[#EBECF8] bg-white transition-colors hover:bg-gray-50 sm:right-4 sm:top-4"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 29 29"
            fill="none"
          >
            <path
              d="M21.5953 7.19995L7.19531 21.6M7.19531 7.19995L21.5953 21.6"
              stroke="#2D2D2D"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <video
          key={item.id}
          src={item.video}
          controls
          controlsList="nodownload noremoteplayback noplaybackrate"
          disablePictureInPicture
          autoPlay
          playsInline
          className="aspect-video w-full"
        >
          <track kind="captions" src="/empty.vtt" srcLang="en" label="English" default />
        </video>

        <div className="flex items-center justify-between px-3 pb-2 pt-2 sm:px-4 sm:pb-3 sm:pt-3">
          <div>
            <p className="font-sans text-[16px] font-normal not-italic tracking-[-1px] text-[#1A1A1A] sm:text-[18px]">
              {item.name}
            </p>
            <p className="font-sans text-[12px] font-normal not-italic tracking-[-1px] text-[#6B7280] sm:text-[14px]">
              {item.project}
            </p>
          </div>
          <Flag flag={item.flag} className="h-[24px] w-[36px] sm:h-[30px] sm:w-[44px]" />
        </div>
      </div>
    </div>
  )
}
